import type { CSSProperties } from 'react';
import type { Column } from '@tanstack/react-table';

import type { ColumnConfig } from '../types';

const LAST_LEFT_PINNED_BORDER: CSSProperties = {
  borderRightWidth: '0.125rem',
  borderRightStyle: 'solid',
  borderRightColor: 'hsl(var(--primary-foreground))',
};

const FIRST_RIGHT_PINNED_BORDER: CSSProperties = {
  borderLeftWidth: '0.125rem',
  borderLeftStyle: 'solid',
  borderLeftColor: 'hsl(var(--primary-foreground))',
};

export interface PinnedCellStyleOptions {
  /** z-index for left-pinned cells (header uses higher value than body). */
  zIndexLeft: number;
  /** z-index for right-pinned cells (header uses higher value than body). */
  zIndexRight: number;
}

/**
 * Sticky position and edge borders for a pinned header or body cell.
 * Offsets come from TanStack Table (`getStart` / `getAfter`) after `columnPinning` state is set.
 */
export function getPinnedCellStyles<TData>(
  column: Column<TData, unknown>,
  columnConfigs: ColumnConfig<TData>[],
  options: PinnedCellStyleOptions
): CSSProperties | undefined {
  const isPinned = column.getIsPinned();
  if (!isPinned) {
    return undefined;
  }

  const visibleConfigs = columnConfigs.filter((c) => !c.hidden);
  const leftPinnedColumns = visibleConfigs.filter((c) => c.pinned === 'left');
  const rightPinnedColumns = visibleConfigs.filter((c) => c.pinned === 'right');
  const isLastLeftPinned =
    isPinned === 'left' && leftPinnedColumns[leftPinnedColumns.length - 1]?.id === column.id;
  const isFirstRightPinned = isPinned === 'right' && rightPinnedColumns[0]?.id === column.id;

  return {
    position: 'sticky',
    left: isPinned === 'left' ? `${column.getStart() / 16}rem` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right') / 16}rem` : undefined,
    zIndex: isPinned === 'left' ? options.zIndexLeft : options.zIndexRight,
    ...(isLastLeftPinned && LAST_LEFT_PINNED_BORDER),
    ...(isFirstRightPinned && FIRST_RIGHT_PINNED_BORDER),
  };
}
